// 警戒狀態回推（五視角評審衝突裁決 C1）— 純函式，好測。
//
// 為什麼需要這個：**模式切換實際寫的是各台的偵測開關（MOTION/OBJECT Enabled），真值本來
// 就住在裝置上**；本機那個 `home`/`away` 只是一個標籤（homeAwayMode.ts）。同一個 attribute
// 另有三個入口會寫它（場景套用、geofence 規則 fire、裝置設定的單台開關），而四個入口之間
// **零仲裁、純 last-write-wins**。改版前完全沒有回推（無 deriveMode/reconcil 之類路徑），
// 所以場景把 Motion.Enabled 關掉之後，首頁仍然顯示「外出（已佈防）」—— UI 會對使用者說謊，
// 而且說謊的方向是「你以為家裡有在警戒，其實沒有」。
//
// 收斂做法不是把標籤變成真值（那要動已上線的寫入路徑，業主 2026-08-19 裁決不動），而是
// **讓標籤能被實況打臉**：進頁時 op:get 各台的期望 attr，不符就在卡上明講並提供「重新套用」。
//
// 判定紀律：
//   - 只有「讀得到 ∧ 與期望不等」才算不符。讀不到（逾時 / 不支援 / 拒絕）＝**未知**，
//     不計入不符 —— 假警報比假綠燈更快讓使用者學會忽略這行字。未知另計，供呼叫端決定是否揭露。
//   - 期望值直接取自 `planArm(devices, mode)` 的 targets，與真正的寫入計畫同源（不另造一份
//     期望表，否則兩份會漂移）。

import type { OpValue } from '../devices/settings/ops';
import type { ArmPlan, ArmTarget } from './homeAwayApply';

/** 單台的「attr id → 讀到的布林值」；undefined ＝ 讀不到。 */
export type AttrReadings = ReadonlyMap<string, boolean | undefined>;

/** 全部台的讀值（device_id → AttrReadings）。 */
export type ArmReadings = ReadonlyMap<string, AttrReadings>;

export interface DriftReport {
  /** 至少讀到一個值、因此有被實際比對的台數（不符率的分母）。 */
  checked: number;
  /** 讀得到且與目前模式期望值不符的 device_id。 */
  mismatched: string[];
  /** 有目標但一個值都讀不到的台數（未知，不算不符）。 */
  unknown: number;
}

export const EMPTY_DRIFT: DriftReport = { checked: 0, mismatched: [], unknown: 0 };

/**
 * 把 report 的值正規化成布林。裝置端對「Enabled」這類旗標可能回 boolean、0/1，或（舊橋接）
 * 字串 —— 直接用 `===` 比 boolean 會把 `1` 判成不符，製造整排假警報。判不出來回 undefined
 * （＝未知，而非 false；把未知當 false 會讓「在家」模式下全部裝置看起來都符合，反向掩蓋缺陷）。
 */
export function normalizeBool(v: OpValue | undefined): boolean | undefined {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? v !== 0 : undefined;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
  }
  return undefined;
}

/** 這台是否與期望不符（任一 attr 讀得到且不等即算）。 */
export function targetMismatch(target: ArmTarget, readings: AttrReadings): boolean {
  return target.ops.some((op) => {
    const got = readings.get(op.id);
    return got !== undefined && got !== op.value;
  });
}

/** 這台是否至少讀到一個值（否則整台歸為未知）。 */
function anyKnown(target: ArmTarget, readings: AttrReadings): boolean {
  return target.ops.some((op) => readings.get(op.id) !== undefined);
}

/** 把逐台讀值彙整成回推報告。plan 提供期望值（與寫入計畫同源）。 */
export function summarizeDrift(plan: ArmPlan, readings: ArmReadings): DriftReport {
  const mismatched: string[] = [];
  let checked = 0;
  let unknown = 0;
  for (const target of plan.targets) {
    const perAttr: AttrReadings = readings.get(target.deviceId) ?? new Map();
    if (!anyKnown(target, perAttr)) {
      unknown += 1;
      continue;
    }
    checked += 1;
    if (targetMismatch(target, perAttr)) mismatched.push(target.deviceId);
  }
  return { checked, mismatched, unknown };
}
